import {
  CF_IDENTIFY_THRESHOLD_CENTS,
  fiscalLetter,
  isValidCuit,
  CARD_METHODS,
  INSTALLMENT_OPTIONS,
  MP_METHODS,
  PAYMENT_METHODS,
  PAYMENT_METHOD_LABELS,
  RECEPTOR_CONDITION_LABELS,
  RECEPTOR_CONDITIONS,
  type CustomerRowDTO,
  type PaymentIntentDTO,
  type PaymentMethod,
  type ReceptorCondition,
  type SaleInput,
  type SaleInvoiceInput,
} from '@bifeai/shared';
import { Button, Callout, Dialog, Field, Input, Select } from '@bifeai/ui';
import { Banknote, BookUser, CreditCard, IdCard, Landmark, QrCode, Receipt, ShieldCheck, Smartphone, Trash2, Wallet } from 'lucide-react';
import { available, CustomerPicker } from '../../components/customers/CustomerPicker';
import { useSupervisors } from '../../lib/customers';
import { useFiscalStatus } from '../../lib/fiscal';
import { MpCharge, useMpCounter, VerifyTransfer } from './MpCharge';
import { useSession } from '../../lib/session';
import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { formatMoney } from '../../lib/format';
import { cashSuggestions } from '../../lib/pos';
import { offlineAllowed } from '../../lib/offline';
import { centsToPesosInput, pesosToCents } from '../../lib/units';

const ICONS: Record<PaymentMethod, ReactNode> = {
  CASH: <Banknote />,
  DEBIT: <CreditCard />,
  CREDIT: <CreditCard />,
  TRANSFER: <Landmark />,
  QR: <QrCode />,
  MP_QR: <QrCode />,
  MP_POINT: <Smartphone />,
  CUENTA_DNI: <IdCard />,
  OTHER: <Wallet />,
  ACCOUNT: <BookUser />,
};

/** Datos extra del cobro: cliente y autorización para fiar por encima del límite. */
export interface ChargeExtra {
  customerId: string | null;
  allowOverLimit?: boolean;
  creditOverride?: { userId: string; pin: string } | null;
  /** Factura electrónica: datos del cliente. null = ticket interno. */
  invoice?: SaleInvoiceInput | null;
}
export type ChargeResult = null | { message: string; code?: string };

const EXTRA = { reference: '', installments: 1, terminal: '', intentId: null as string | null };

interface Row {
  method: PaymentMethod;
  amount: string;
  reference: string;
  installments: number;
  terminal: string;
  /** Cobro de Mercado Pago confirmado (QR, Point o transferencia verificada). */
  intentId: string | null;
}

/**
 * Cobro: un toque elige el medio de pago por el total. Para pagar con dos medios
 * (parte efectivo, parte tarjeta) se agrega otra línea. En efectivo, "paga con" calcula el vuelto.
 */
export function PaymentDialog({
  totalCents,
  enabled: enabledManual,
  branchId,
  terminals = [],
  offline = false,
  onClose,
  onCharge,
}: {
  totalCents: number;
  enabled: PaymentMethod[];
  /** Sucursal de la caja (para los cobros de Mercado Pago). */
  branchId: string | null;
  /** Posnets de la carnicería: si hay más de uno se pregunta con cuál se cobró. */
  terminals?: string[];
  /** Sin internet: solo los medios que no necesitan conexión, sin fiado ni factura con datos del cliente. */
  offline?: boolean;
  onClose: () => void;
  onCharge: (payments: SaleInput['payments'], cashTenderedCents: number | null, extra: ChargeExtra) => Promise<ChargeResult>;
}) {
  const { can } = useSession();
  const canAuthorize = can('customers.manage');
  // Mercado Pago aparece solo si la carnicería conectó su cuenta (QR) o tiene un posnet Point en esta sucursal.
  const mpCounter = useMpCounter(branchId).data;
  const mpOn = (m: PaymentMethod) => (m === 'MP_QR' ? !!mpCounter?.qr : m === 'MP_POINT' ? (mpCounter?.terminals.length ?? 0) > 0 : false);
  const enabled = PAYMENT_METHODS.filter((m) =>
    offline ? enabledManual.includes(m) && offlineAllowed(m) : MP_METHODS.includes(m) ? mpOn(m) : enabledManual.includes(m),
  );
  const splitMethods = enabled.filter((m) => !MP_METHODS.includes(m));
  const [verified, setVerified] = useState<PaymentIntentDTO | null>(null);
  const [customer, setCustomer] = useState<CustomerRowDTO | null>(null);
  const [showCustomer, setShowCustomer] = useState(false);
  const [selfAuth, setSelfAuth] = useState(false);
  const [supervisorId, setSupervisorId] = useState('');
  const [pin, setPin] = useState('');
  const [limitError, setLimitError] = useState<string | null>(null);
  const [rows, setRows] = useState<Row[]>([
    {
      method: enabled.includes('CASH') ? 'CASH' : enabled[0]!,
      amount: centsToPesosInput(totalCents),
      ...EXTRA,
    },
  ]);
  const [tendered, setTendered] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const split = rows.length > 1;
  const setRow = (i: number, patch: Partial<Row>) => setRows((rs) => rs.map((x, j) => (j === i ? { ...x, ...patch } : x)));
  const fiscalStatus = useFiscalStatus().data;
  const canInvoice = !offline && !!fiscalStatus?.enabled && fiscalStatus.ready && fiscalStatus.invoiceMode !== 'TICKET';
  const [wantInvoice, setWantInvoice] = useState(false);
  const invoicing = canInvoice && (fiscalStatus.invoiceMode === 'INVOICE' || wantInvoice);
  const [receptor, setReceptor] = useState<{ condition: ReceptorCondition; doc: string; name: string }>({ condition: 'CF', doc: '', name: '' });
  const docDigits = receptor.doc.replace(/\D/g, '');
  const receptorError = !invoicing
    ? null
    : receptor.condition !== 'CF'
      ? isValidCuit(docDigits)
        ? null
        : 'Cargá el CUIT del cliente'
      : docDigits.length > 0 && !(docDigits.length === 11 ? isValidCuit(docDigits) : docDigits.length >= 7 && docDigits.length <= 8)
        ? 'DNI o CUIT inválido'
        : totalCents >= CF_IDENTIFY_THRESHOLD_CENTS && docDigits.length === 0
          ? 'Desde $ 10.000.000 hay que identificar al cliente (DNI o CUIT)'
          : null;

  const amounts = rows.map((r) => pesosToCents(r.amount) ?? 0);
  const paid = amounts.reduce((a, b) => a + b, 0);
  const remaining = totalCents - paid;
  const cashCents = rows.reduce((a, r, i) => a + (r.method === 'CASH' ? amounts[i]! : 0), 0);
  const tenderedCents = tendered.trim() ? pesosToCents(tendered) : null;
  const change = tenderedCents !== null && cashCents > 0 ? tenderedCents - cashCents : null;
  const accountCents = rows.reduce((a, r, i) => a + (r.method === 'ACCOUNT' ? amounts[i]! : 0), 0);
  const overLimit = accountCents > 0 && customer !== null && accountCents > available(customer);
  const needsAuth = (overLimit || limitError !== null) && accountCents > 0;
  const supervisors = useSupervisors(needsAuth && !canAuthorize);
  const authorized = !needsAuth || (canAuthorize ? selfAuth : supervisorId !== '' && /^\d{4,8}$/.test(pin));
  const ok =
    remaining === 0 &&
    rows.every((_, i) => amounts[i]! > 0) &&
    (change === null || change >= 0) &&
    (accountCents === 0 || customer !== null) &&
    rows.every((r) => !MP_METHODS.includes(r.method) || r.intentId) &&
    receptorError === null &&
    authorized;

  useEffect(() => {
    setTendered('');
  }, [cashCents]);

  // Cuando Mercado Pago confirma el pago, la venta se cierra sola.
  const [autoSubmit, setAutoSubmit] = useState(false);
  useEffect(() => {
    if (autoSubmit && ok && !busy) {
      setAutoSubmit(false);
      void submit();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoSubmit, ok, busy]);

  function choose(method: PaymentMethod) {
    setVerified(null);
    if (!split) setRows([{ method, amount: centsToPesosInput(totalCents), ...EXTRA, terminal: rows[0]!.terminal }]);
  }

  async function submit(e?: FormEvent) {
    e?.preventDefault();
    if (!ok || busy) return;
    setBusy(true);
    setError(null);
    const res = await onCharge(
      rows.map((r, i) => ({
        method: r.method,
        amountCents: amounts[i]!,
        reference: r.reference.trim() || null,
        installments: r.method === 'CREDIT' ? r.installments : null,
        intentId: r.intentId,
        terminal: CARD_METHODS.includes(r.method) ? r.terminal || terminals[0] || null : null,
      })),
      cashCents > 0 ? (tenderedCents ?? cashCents) : null,
      {
        customerId: customer?.id ?? null,
        allowOverLimit: needsAuth && canAuthorize ? selfAuth : undefined,
        creditOverride: needsAuth && !canAuthorize && supervisorId ? { userId: supervisorId, pin } : null,
        invoice: invoicing
          ? { receptorCondition: receptor.condition, docNumber: docDigits || null, receptorName: receptor.name.trim() || null }
          : null,
      },
    );
    if (res?.code === 'CREDIT_LIMIT') {
      setLimitError(res.message);
      setPin('');
    } else if (res) {
      setError(res.message);
      setPin('');
    }
    setBusy(false);
  }

  return (
    <Dialog open onOpenChange={(o) => !o && !busy && onClose()} title="Cobrar" wide>
      <form className="bf-form" onSubmit={submit} noValidate>
        {error && <Callout tone="danger">{error}</Callout>}
        {offline && (
          <Callout tone="warning">
            Sin internet: cobrá en efectivo, con tarjeta en el posnet o por transferencia. La venta se guarda y se sube sola cuando vuelva la
            conexión.
          </Callout>
        )}
        <div className="bf-pay-total">
          <span>Total a cobrar</span>
          <strong className="bf-num">{formatMoney(totalCents)}</strong>
        </div>

        {!split && (
          <div className="bf-pay-methods" role="radiogroup" aria-label="Medio de pago">
            {enabled.map((m) => (
              <button key={m} type="button" role="radio" aria-checked={rows[0]!.method === m} className="bf-pay-method" onClick={() => choose(m)}>
                {ICONS[m]}
                <span>{PAYMENT_METHOD_LABELS[m]}</span>
              </button>
            ))}
          </div>
        )}

        {split && (
          <div className="bf-pay-rows">
            {rows.map((r, i) => (
              <div key={i} className="bf-pay-row">
                <Select
                  aria-label={`Medio de pago ${i + 1}`}
                  value={r.method}
                  onChange={(e) => setRows((rs) => rs.map((x, j) => (j === i ? { ...x, method: e.target.value as PaymentMethod } : x)))}
                >
                  {splitMethods.map((m) => (
                    <option key={m} value={m}>
                      {PAYMENT_METHOD_LABELS[m]}
                    </option>
                  ))}
                </Select>
                <Input
                  aria-label={`Importe ${i + 1}`}
                  inputMode="decimal"
                  value={r.amount}
                  onChange={(e) => setRows((rs) => rs.map((x, j) => (j === i ? { ...x, amount: e.target.value } : x)))}
                />
                <button
                  type="button"
                  className="bf-icon-btn"
                  aria-label="Quitar este pago"
                  onClick={() => setRows((rs) => (rs.length > 1 ? rs.filter((_, j) => j !== i) : rs))}
                >
                  <Trash2 />
                </button>
              </div>
            ))}
            <p className={remaining === 0 ? 'bf-text-success' : 'bf-text-warning'}>
              {remaining === 0
                ? 'Los pagos cubren el total.'
                : remaining > 0
                  ? `Faltan ${formatMoney(remaining)}`
                  : `Sobran ${formatMoney(-remaining)}`}
            </p>
          </div>
        )}

        {(accountCents > 0 || showCustomer || customer) && (
          <div className="bf-pay-customer">
            <span className="bf-label">{accountCents > 0 ? 'Cliente (para anotar el fiado)' : 'Cliente (opcional)'}</span>
            <CustomerPicker
              value={customer}
              onChange={(c) => {
                setCustomer(c);
                setLimitError(null);
                // Cliente con datos fiscales: se proponen para la factura.
                if (c && canInvoice) {
                  const cond = c.ivaCondition ?? 'CF';
                  setReceptor({ condition: cond, doc: c.docNumber ?? '', name: c.name });
                  if (cond !== 'CF') setWantInvoice(true);
                }
              }}
              autoFocus={accountCents > 0 && !customer}
            />
          </div>
        )}
        {!offline && accountCents === 0 && !showCustomer && !customer && (
          <button type="button" className="bf-link-btn" onClick={() => setShowCustomer(true)}>
            + Asignar un cliente a esta venta
          </button>
        )}

        {canInvoice && (
          <div className="bf-pay-invoice">
            {fiscalStatus.invoiceMode === 'ASK' && (
              <div className="bf-chips" role="group" aria-label="Comprobante">
                <button type="button" className="bf-chip" aria-pressed={!wantInvoice} onClick={() => setWantInvoice(false)}>
                  Ticket
                </button>
                <button type="button" className="bf-chip" aria-pressed={wantInvoice} onClick={() => setWantInvoice(true)}>
                  <Receipt size={14} aria-hidden /> Factura
                </button>
              </div>
            )}
            {invoicing && (
              <>
                <div className="bf-form-row">
                  <Field label={`Factura ${fiscalLetter(fiscalStatus.ivaCondition ?? 'MONO', receptor.condition)} a`}>
                    <Select value={receptor.condition} onChange={(e) => setReceptor({ ...receptor, condition: e.target.value as ReceptorCondition })}>
                      {RECEPTOR_CONDITIONS.map((c) => (
                        <option key={c} value={c}>
                          {RECEPTOR_CONDITION_LABELS[c]}
                        </option>
                      ))}
                    </Select>
                  </Field>
                  <Field label={receptor.condition === 'CF' ? 'DNI o CUIT (opcional)' : 'CUIT'} error={receptorError ?? undefined}>
                    <Input inputMode="numeric" value={receptor.doc} onChange={(e) => setReceptor({ ...receptor, doc: e.target.value })} />
                  </Field>
                  {(receptor.condition !== 'CF' || docDigits) && (
                    <Field label="Nombre o razón social">
                      <Input value={receptor.name} onChange={(e) => setReceptor({ ...receptor, name: e.target.value })} />
                    </Field>
                  )}
                </div>
                {fiscalStatus.environment === 'HOMO' && <p className="bf-hint">ARCA de prueba: la factura no tiene validez fiscal.</p>}
              </>
            )}
          </div>
        )}
        {fiscalStatus?.enabled && !fiscalStatus.ready && fiscalStatus.invoiceMode !== 'TICKET' && (
          <p className="bf-hint">La facturación electrónica no está lista: sale ticket interno. Se completa en Configuración → Facturación ARCA.</p>
        )}

        {needsAuth && customer && (
          <div className="bf-pay-auth">
            <Callout tone="warning">
              {limitError ??
                (customer.creditLimitCents === 0
                  ? `${customer.name} no tiene fiado habilitado.`
                  : `Supera el límite de ${customer.name}: puede fiar ${formatMoney(available(customer))} y esta compra anota ${formatMoney(accountCents)}.`)}{' '}
              Hace falta la autorización de un dueño o encargado.
            </Callout>
            {canAuthorize ? (
              <label className="bf-check">
                <input type="checkbox" checked={selfAuth} onChange={(e) => setSelfAuth(e.target.checked)} /> <ShieldCheck size={16} aria-hidden />{' '}
                Autorizo superar el límite
              </label>
            ) : (supervisors.data?.length ?? 0) === 0 ? (
              <p className="bf-cell-sub">Ningún dueño o encargado tiene PIN cargado. Lo crean desde Mi cuenta.</p>
            ) : (
              <div className="bf-form-row">
                <Field label="Autoriza">
                  <Select value={supervisorId} onChange={(e) => setSupervisorId(e.target.value)}>
                    <option value="">Elegí quién autoriza</option>
                    {supervisors.data?.map((u) => (
                      <option key={u.id} value={u.id}>
                        {u.name}
                      </option>
                    ))}
                  </Select>
                </Field>
                <Field label="PIN">
                  <Input
                    type="password"
                    inputMode="numeric"
                    autoComplete="off"
                    value={pin}
                    onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 8))}
                  />
                </Field>
              </div>
            )}
          </div>
        )}

        {!split && MP_METHODS.includes(rows[0]!.method) && branchId && (
          <MpCharge
            key={rows[0]!.method}
            kind={rows[0]!.method === 'MP_QR' ? 'QR' : 'POINT'}
            amountCents={totalCents}
            branchId={branchId}
            terminals={mpCounter?.terminals ?? []}
            onPaid={(id) => {
              setRow(0, { intentId: id });
              setAutoSubmit(true);
            }}
          />
        )}
        {!split && rows[0]!.method === 'TRANSFER' && mpCounter?.connected && branchId && (
          <VerifyTransfer
            amountCents={totalCents}
            branchId={branchId}
            verified={verified}
            onVerified={(i) => {
              setVerified(i);
              setRow(0, { intentId: i.id, reference: i.payer ?? '' });
            }}
          />
        )}
        {rows.map((r, i) =>
          CARD_METHODS.includes(r.method) ? (
            <div key={`card-${i}`} className="bf-form-row bf-pay-card">
              {terminals.length > 1 && (
                <Field label={split ? `Posnet (${PAYMENT_METHOD_LABELS[r.method]})` : 'Posnet'}>
                  <Select value={r.terminal || terminals[0]} onChange={(e) => setRow(i, { terminal: e.target.value })}>
                    {terminals.map((t) => (
                      <option key={t} value={t}>
                        {t}
                      </option>
                    ))}
                  </Select>
                </Field>
              )}
              {r.method === 'CREDIT' && (
                <Field label="Cuotas">
                  <Select value={r.installments} onChange={(e) => setRow(i, { installments: Number(e.target.value) })}>
                    {INSTALLMENT_OPTIONS.map((n) => (
                      <option key={n} value={n}>
                        {n === 1 ? '1 pago' : `${n} cuotas`}
                      </option>
                    ))}
                  </Select>
                </Field>
              )}
              <Field label="N.º de cupón (opcional)" hint="El que imprime el posnet. Sirve si hay un reclamo.">
                <Input inputMode="numeric" value={r.reference} onChange={(e) => setRow(i, { reference: e.target.value })} />
              </Field>
            </div>
          ) : !split && r.method !== 'CASH' && r.method !== 'ACCOUNT' && !MP_METHODS.includes(r.method) && !r.intentId ? (
            <Input
              key={`ref-${i}`}
              aria-label="Referencia (opcional)"
              placeholder="Referencia (opcional): n.º de operación…"
              value={r.reference}
              onChange={(e) => setRow(i, { reference: e.target.value })}
            />
          ) : null,
        )}
        {rows.some((r) => CARD_METHODS.includes(r.method)) && (
          <p className="bf-hint">Cobrá en el posnet el mismo importe y confirmá acá cuando diga “Aprobado”.</p>
        )}
        {rows.some((r) => r.method === 'CUENTA_DNI') && (
          <p className="bf-hint">Confirmá cuando la app Cuenta DNI Comercios (o el posnet) muestre el pago aprobado.</p>
        )}

        {cashCents > 0 && (
          <div className="bf-pay-cash">
            <label className="bf-label" htmlFor="pay-with">
              Paga con (efectivo)
            </label>
            <div className="bf-chips">
              {cashSuggestions(cashCents).map((v) => (
                <button
                  key={v}
                  type="button"
                  className="bf-chip"
                  aria-pressed={tenderedCents === v}
                  onClick={() => setTendered(centsToPesosInput(v))}
                >
                  {formatMoney(v)}
                </button>
              ))}
            </div>
            <Input
              id="pay-with"
              inputMode="decimal"
              value={tendered}
              onChange={(e) => setTendered(e.target.value)}
              placeholder={`$ ${centsToPesosInput(cashCents)}`}
              autoFocus
            />
            {change !== null && (
              <div className={`bf-pay-change ${change < 0 ? 'bf-text-danger' : ''}`}>
                {change < 0 ? (
                  `Faltan ${formatMoney(-change)}`
                ) : (
                  <>
                    Vuelto <strong className="bf-num">{formatMoney(change)}</strong>
                  </>
                )}
              </div>
            )}
          </div>
        )}

        <div className="bf-form-actions">
          {!split && enabled.length > 1 && (
            <Button
              variant="ghost"
              icon={<Smartphone />}
              style={{ marginRight: 'auto' }}
              onClick={() =>
                setRows((rs) => [
                  { ...rs[0]!, amount: '', method: MP_METHODS.includes(rs[0]!.method) ? splitMethods[0]! : rs[0]!.method, intentId: null },
                  {
                    method: splitMethods.find((m) => m !== rs[0]!.method) ?? splitMethods[0]!,
                    amount: '',
                    ...EXTRA,
                  },
                ])
              }
            >
              Pagar con dos medios
            </Button>
          )}
          {split && rows.length < 4 && (
            <Button
              variant="ghost"
              style={{ marginRight: 'auto' }}
              onClick={() =>
                setRows((rs) => [
                  ...rs,
                  {
                    method: splitMethods[0]!,
                    amount: remaining > 0 ? centsToPesosInput(remaining) : '',
                    ...EXTRA,
                  },
                ])
              }
            >
              + Otro medio
            </Button>
          )}
          <Button onClick={onClose} disabled={busy}>
            Volver
          </Button>
          <Button type="submit" variant="primary" loading={busy} disabled={!ok}>
            Cobrar {formatMoney(totalCents)}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
